"use strict";

const View = require('./View');

const Factory = require('./ActiveFactory');

const {
  PLUGIN_ADDED,
  PLUGIN_REMOVED,
  CREATOR_ADDED,
  CREATOR_REMOVED,
  CREATOR_CHANGED,
  DEFAULT
} = require('./Constants');

const EMPTY_ARRAY = Object.freeze([]);

class Plugins {
  constructor() {
    this.types = {};
    this.creators = {};
    this.cache = {};
    this.typesLookup = {};
    this.typesVersion = {}; // a change index

    this.globalListeners = [];
    this.listeners = {};
    this.factories = {};
    this.views = {}; // Expose factory strategies. Putting this as an instance level
    // arguments so it can be accessed through the library's singleton
    // Plugins.ALL call

    this.ALL = Factory.ALL;
    this.NONE = Factory.NONE;
    this.ARGS = Factory.ARGS;
    this.MERGE = Factory.MERGE;
    this.LOOKUP = Factory.LOOKUP;
    this.mergeStrategy = Factory.mergeStrategy;
    this.reactStrategy = Factory.reactStrategy;
    this.PLUGIN_ADDED = PLUGIN_ADDED;
    this.PLUGIN_REMOVED = PLUGIN_REMOVED;
    this.CREATOR_ADDED = CREATOR_ADDED;
    this.CREATOR_REMOVED = CREATOR_REMOVED;
    this.CREATOR_CHANGED = CREATOR_CHANGED;
  }
  /**
   * Adds a filter/mutator to the plugins
   * @param {function} mutator This function should accept ( type, plugin )
   * and return the plugin, a mutated plugin, or null. If null is returned, the
   * it is effectively filtered from the plugin mechanism.
   */


  addMutator(mutator) {
    if (this.mutators == null) {
      this.mutators = [mutator];
      return;
    }

    if (!this.mutators.includes(mutator)) {
      this.mutators.push(mutator);
    }
  }
  /**
   * Removes a filter/mutator to the plugins
   */


  removeMutator(mutator) {
    const index = this.mutators ? this.mutators.indexOf(mutator) : -1;

    if (index >= 0) {
      this.mutators.splice(index, 1);
    }
  }
  /**
   * Filters/mutates a plugin being added to the plugin mechanism
   * @param {String} type the type of plugin
   * @param {Plugin} plugin the plugin object
   * @returns plugin, a variant of plugin or null
   */


  runMutators(type, plugin) {
    if (this.mutators == null) {
      return plugin;
    }

    let nPlugin = plugin;

    for (let n = 0; n < this.mutators.length; n++) {
      nPlugin = this.mutators[n](type, nPlugin);

      if (nPlugin == null) {
        return null;
      }
    }

    return nPlugin;
  }
  /**
   * Adds a plugin to the type plugin set. This will fire events to listeners
   * @param {string} type the type of plugin, like 'Action', 'ModelEditor', etc
   * @param {object} plg the plugin to add. If it has an 'id' property, it must
   * be unique in the current ids or it will be rejected. It is good practice to have
   * ids, so they can be removed using the id name.
   */


  add(type, plg) {
    if (this.types[type] == null) {
      this.types[type] = [];
      this.typesLookup[type] = {};
      this.typesVersion[type] = 0;
    }

    const plugin = this.runMutators(type, plg);

    if (plugin == null) {
      return null;
    }

    if (this.types[type].includes(plugin)) {
      // eslint-disable-next-line no-console
      console.log('Plugin already added', type, plugin);
      return false; // already added
    }

    if (typeof plugin.id === 'string') {
      if (this.typesLookup[type][plugin.id]) {
        // eslint-disable-next-line no-console
        console.log('Ignoring Plugin with same id already added.', type, plugin);
        return false; // already added
      }

      this.typesLookup[type][plugin.id] = plugin;
    } // Check Type??? 


    this.types[type].push(plugin);
    this.typesVersion[type]++;
    this.sendEvent({
      type: PLUGIN_ADDED,
      pluginType: type,
      plugin,
      plugins: this
    });
    return true;
  }
  /**
   * This will call add( ... )
   **/


  addPlugin(...rest) {
    this.add(...rest);
  }
  /**
   * If the plugin has an id string, it will remove the old plugin add the new plugin.
   * Events for both remove and add will be fired
   */


  replace(type, plugin) {
    if (plugin == null) {
      return false;
    }

    if (typeof plugin.id === 'string') {
      this.remove(type, plugin.id);
    }

    return this.add(type, plugin);
  }
  /**
   * This will call replace( ... )
   **/


  replacePlugin(...rest) {
    this.replace(...rest);
  }

  remove(type, plugin) {
    if (this.types[type] == null) {
      return false;
    }

    let idx = -1;

    if (typeof plugin === 'string') {
      // remove by type.
      idx = this.types[type].findIndex(p => p.id === plugin);
    } else {
      idx = this.types[type].indexOf(plugin);
    }

    if (idx < 0) {
      return false;
    }

    const plg = this.types[type][idx];

    if (typeof plg.id === 'string') {
      delete this.typesLookup[type][plg.id];
    }

    this.types[type].splice(idx, 1);
    this.typesVersion[type]++;
    this.sendEvent({
      type: PLUGIN_REMOVED,
      pluginType: type,
      plugin,
      plugins: this
    });
    return plg;
  }
  /**
   * This will call remove( ... )
   **/


  removePlugin(...rest) {
    this.remove(...rest);
  }
  /**
   * Adds a Plugin creator that can be used to lazily contribute
   * plugins to a type. The result from each create() is cached, and
   * create() will only be requeried if the callback given to on() is
   * invoked.
   * @param {String} type the plugin type
   * @param {PluginCreator} creator the creator should have three methods, 
   * on(), off() and create(). The on and off methods are used to register
   * and deregister a function to be called when the Plugin creator changes.
   * The create() is used to retreive the plugins. These plugins should look
   * and behave like any plugin added via addPlugin( type, plg ).
   * @returns 
   */


  addCreator(type, creator) {
    if (this.creators[type] == null) {
      this.creators[type] = [];
    } // Already added


    if (this.creators[type].find(({
      creator: c
    }) => c === creator)) {
      return;
    }

    const entry = {
      creator
    };

    entry.onChange = () => {
      this.typesVersion[type]++;
      entry.cached = null;
      this.sendEvent({
        type: CREATOR_CHANGED,
        pluginType: type,
        creator,
        plugins: this
      });
    };

    this.creators[type].push(entry);

    if (this.typesVersion[type] == null) {
      this.typesVersion[type] = 1;
    } else {
      this.typesVersion[type]++;
    }

    creator.on(entry.onChange);
    this.sendEvent({
      type: CREATOR_ADDED,
      pluginType: type,
      creator,
      plugins: this
    });
  }
  /**
   * Removes a PluginCreator
   * @param {string} type The type of plugin
   * @param {PluginCreator} creator The creator to remove
   * @returns 
   */


  removeCreator(type, creator) {
    if (this.creators[type] == null) {
      return;
    }

    const idx = this.creators[type].findIndex(({
      creator: c
    }) => c === creator);

    if (idx < 0) {
      return;
    }

    const entry = this.creators[type][idx];
    entry.creator.off(entry.onChange);
    this.creators[type].splice(idx, 1);
    this.typesVersion[type]++;
    this.sendEvent({
      type: CREATOR_REMOVED,
      pluginType: type,
      creator,
      plugins: this
    });
  }
  /**
   * Sends the event to all globalListener and all listeners registered against
   * the plugin type.
   */


  sendEvent(evt) {
    this.globalListeners.forEach(listener => {
      listener(evt);
    });

    if (this.listeners[evt.pluginType] != null) {
      this.listeners[evt.pluginType].forEach(listener => {
        listener(evt);
      });
    }
  }
  /**
   * Returns an array of plugins of the given type. If a criteria function
   * is given, it will be used to filter the array.
   */


  get(type, criteria) {
    let arr = this.types[type] || EMPTY_ARRAY;
    const creators = this.creators[type];

    if (creators) {
      // lets check cache first.
      if (this.cache[type] && this.cache[type].version === this.getVersion(type)) {
        arr = this.cache[type].plugins;
      } else {
        // Add the creators to the list
        for (let n = 0; n < creators.length; n++) {
          const entry = creators[n];

          if (entry.cached == null) {
            try {
              const plgs = entry.creator.create() || [];
              const cached = [];

              for (let n = 0; n < plgs.length; n++) {
                const p = this.runMutators(type, plgs[n]);

                if (p) {
                  cached.push(p);
                }
              }

              entry.cached = cached;
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('PluginCreator threw an error creating plugins for type:', type, entry.creator);
            }
          }

          if (entry.cached.length > 0) {
            arr = [...arr, ...entry.cached];
          }
        }

        this.cache[type] = {
          version: this.getVersion(type),
          plugins: arr
        };
      }
    }

    if (criteria == null) {
      return arr;
    }

    return arr.filter(criteria);
  }
  /**
   * Returns the change version the plugin type. This is the number
   * of times add and remove have been called on the type.
   */


  getVersion(type) {
    const version = this.typesVersion[type];
    return version == null ? 0 : version;
  }
  /**
   * Adds a listener to the type. If this is invoke with just a function ( plugins.on( listener ) ), 
   * the listener will be added to the global listener list
   * @return a function to remove the listener. This function will call off()
   */


  on(type, listener) {
    if (typeof type === 'function') {
      this.globalListeners.push(type);
      return () => {
        this.off(type);
      };
    }

    if (this.listeners[type] == null) {
      this.listeners[type] = [];
    }

    if (!this.listeners[type].includes(listener)) {
      this.listeners[type].push(listener);
    }

    return () => {
      this.off(type, listener);
    };
  }
  /**
   * Removes a listener from the type. If this is invoke with just a function ( plugins.off( listener ) ), 
   * the listener will be removed from the global listener list
   */


  off(type, listener) {
    if (typeof type === 'function') {
      const gidx = this.globalListeners.indexOf(type);

      if (gidx >= 0) {
        this.globalListeners.splice(gidx, 1);
      }

      return;
    }

    if (this.listeners[type] == null) {
      return;
    }

    const idx = this.listeners[type].indexOf(listener);

    if (idx >= 0) {
      this.listeners[type].splice(idx, 1);
    }
  }
  /**
   * Creates a managed factory. Any plugin added via add()
   * will be added to a factory as well.
   * @param {string} type the type of the plugin to build a factory for
   * @param {any} options the factory options with addition options (pluginKey, pluginTypeKey, filter )
   * This is passed to the factory constructor
   * @param {string} options.name the name of the for the factory. If a factory with the
   * type/name combination already exists, this will throw an error. If name is specified as 'null',
   * the factory will not be managed and you should call stop() on it. If the name is not set, 'default'
   * will be used
   */


  createFactory(type, options, throwError = false) {
    const opts = typeof options === 'function' ? {
      strategy: options
    } : { ...options
    };
    let name = opts.name;

    if (name === undefined) {
      name = DEFAULT;
    } // set the factory name for debug purpose with the type


    opts.name = `${type}-${name || 'unmanaged'}`;

    if (this.factories[type] == null) {
      this.factories[type] = {};
    } else if (name && this.factories[type][name] != null) {
      if (throwError === true) {
        throw Error(`Factory for ${type} called ${name} already exists.`);
      }

      return this.factories[type][name];
    }

    const f = new Factory(this, type, opts);

    if (name !== null) {
      this.factories[type][name] = f;
    }

    return f;
  }
  /**
   * Returns an existing factory or creates new one. 
   * @param {string} type the type of the plugin to get/build a factory for
   * @param {any} options the factory options. This is passed to the constructor if
   * a new factory is made
   * @param {string} name the name of the for the factory.
   */
  // getOrCreateFactory( type, options, name = DEFAULT ) {
  //   return this.getFactory( type, name ) || this.createFactory( type, options, name )
  // }

  /**
   * Returns an existing factory . 
   * @param {string} type the type of the plugin 
   * @param {string} name the name of the for the factory.
   **/


  getFactory(type, name = DEFAULT) {
    if (this.factories[type] && this.factories[type][name]) {
      return this.factories[type][name];
    }

    return null;
  }
  /**
   * Creates a view with the given type and options.
   * If throwError is false and a view of the same name 
   * already exists, it will be returned. Otherwise, the
   * an error will be thrown.
   */


  createView(type, options, throwError = false) {
    const opts = options || {};
    const name = opts.name === undefined ? DEFAULT : opts.name;

    if (this.views[type] == null) {
      this.views[type] = {};
    }

    if (name && this.views[type][name]) {
      if (throwError === true) {
        throw Error(`View for ${type} called ${name} already exists.`);
      }

      return this.views[type][name];
    }

    const view = new View(this, type, opts);

    if (name !== null) {
      this.views[type][name] = view;
    }

    return view;
  }

  getView(type, name = DEFAULT) {
    if (this.views[type] && this.views[type][name]) {
      return this.views[type][name];
    }

    return null;
  }

} // Add statics


Plugins.PLUGIN_ADDED = PLUGIN_ADDED;
Plugins.PLUGIN_REMOVED = PLUGIN_REMOVED;
Plugins.CREATOR_ADDED = CREATOR_ADDED;
Plugins.CREATOR_REMOVED = CREATOR_REMOVED;
Plugins.CREATOR_CHANGED = CREATOR_CHANGED;
module.exports = Plugins;